// internal/KnowledgePage.jsx — Knowledge Base (§22) : mémoire d'entreprise filtrée par droits, injectée dans les Copilots.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import InternalLayout, { S, IT } from "@/internal/InternalLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const H = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });
const CATS = {
  architecture: { l: "Architecture", c: "#38BDF8" }, regle_metier: { l: "Règle métier", c: "#F59E0B" },
  decision: { l: "Décision", c: "#A78BFA" }, procedure: { l: "Procédure", c: "#2EE27B" }, connaissance_ops: { l: "Connaissance ops", c: "#FB923C" },
};
const WS_OPTS = ["global", "tech", "product", "community", "field"];

function Editor({ item, onClose, onSaved }) {
  const [f, setF] = useState(item || { title: "", content: "", category: "connaissance_ops", visibility: ["global"] });
  const [busy, setBusy] = useState(false);
  const toggleVis = (v) => setF((p) => ({ ...p, visibility: p.visibility.includes(v) ? p.visibility.filter((x) => x !== v) : [...p.visibility, v] }));
  const save = async () => {
    if (!f.title.trim() || !f.content.trim()) { toast.error("Titre et contenu requis"); return; }
    setBusy(true);
    try {
      const r = await fetch(item ? `${API}/knowledge/${item.kb_id}` : `${API}/knowledge`, {
        method: item ? "PUT" : "POST", headers: H(), body: JSON.stringify(f),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error);
      toast.success(item ? "Article mis à jour" : "Article créé");
      onSaved(); onClose();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };
  const inp = { width: "100%", boxSizing: "border-box", background: "#0D1310", border: `1px solid ${IT.border}`, borderRadius: 10, padding: "10px 12px", color: IT.text, fontSize: 13, outline: "none", fontFamily: "inherit" };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...S.card, padding: 20, maxWidth: 560, width: "100%", maxHeight: "86vh", overflowY: "auto" }} data-testid="kb-editor">
        <h2 style={{ ...S.h2, fontSize: 16, marginBottom: 12 }}>{item ? "✏️ Modifier l'article" : "➕ Nouvel article"}</h2>
        <input data-testid="kb-title" style={inp} placeholder="Titre" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <textarea data-testid="kb-content" style={{ ...inp, marginTop: 10, minHeight: 140, resize: "vertical" }} placeholder="Contenu (décision, procédure, règle…)" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
          {Object.entries(CATS).map(([k, c]) => (
            <button key={k} data-testid={`kb-cat-${k}`} onClick={() => setF({ ...f, category: k })}
              style={{ padding: "5px 11px", borderRadius: 999, fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${f.category === k ? c.c : "rgba(139,163,148,0.3)"}`, background: f.category === k ? `${c.c}18` : "transparent", color: f.category === k ? c.c : IT.muted }}>{c.l}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: IT.muted, margin: "12px 0 6px", fontWeight: 800 }}>VISIBILITÉ (droits d'accès)</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {WS_OPTS.map((v) => (
            <button key={v} data-testid={`kb-vis-${v}`} onClick={() => toggleVis(v)}
              style={{ padding: "5px 11px", borderRadius: 999, fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${f.visibility.includes(v) ? IT.green : "rgba(139,163,148,0.3)"}`, background: f.visibility.includes(v) ? "rgba(46,226,123,0.12)" : "transparent", color: f.visibility.includes(v) ? IT.green : IT.muted }}>
              {v === "global" ? "🌍 Global" : v}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ ...S.ghost, borderColor: "rgba(139,163,148,0.4)", color: IT.muted }}>Annuler</button>
          <button data-testid="kb-save" onClick={save} disabled={busy} style={S.btn}>💾 Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

export default function KnowledgePage() {
  const [d, setD] = useState(null);
  const [cat, setCat] = useState("all");
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/knowledge`, { headers: H() }).then((r) => r.json()).then((x) => x.ok && setD(x)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (kb) => {
    if (!window.confirm(`Supprimer « ${kb.title} » ?`)) return;
    const r = await fetch(`${API}/knowledge/${kb.kb_id}`, { method: "DELETE", headers: H() });
    const x = await r.json();
    if (x.ok) { toast.success("Article supprimé"); load(); } else toast.error(x.error);
  };

  const items = (d?.items || []).filter((k) => cat === "all" || k.category === cat);
  return (
    <InternalLayout title="Knowledge Base" subtitle="La mémoire d'entreprise d'AYA — injectée automatiquement dans les Copilots, filtrée selon les droits.">
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
        {[["all", "Tout"], ...Object.entries(CATS).map(([k, c]) => [k, c.l])].map(([k, l]) => (
          <button key={k} data-testid={`kb-filter-${k}`} onClick={() => setCat(k)}
            style={{ padding: "6px 13px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${cat === k ? IT.green : "rgba(139,163,148,0.3)"}`, background: cat === k ? "rgba(46,226,123,0.12)" : "transparent", color: cat === k ? IT.green : IT.muted }}>{l}</button>
        ))}
        <span style={{ flex: 1 }} />
        {d?.ceo && <button data-testid="kb-new" onClick={() => setCreating(true)} style={S.btn}>➕ Nouvel article</button>}
      </div>
      {!d && <div style={{ color: IT.muted, fontSize: 13 }}>Chargement…</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }} data-testid="kb-grid">
        {items.map((k) => {
          const c = CATS[k.category] || { l: k.category, c: IT.muted };
          return (
            <div key={k.kb_id} style={{ ...S.card, padding: 16 }} data-testid={`kb-item-${k.kb_id}`}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 9.5, fontWeight: 900, color: c.c, border: `1px solid ${c.c}55`, borderRadius: 999, padding: "2px 9px" }}>{c.l}</span>
                {(k.visibility || []).map((v) => (
                  <span key={v} style={{ fontSize: 9, fontWeight: 800, color: IT.muted, border: "1px solid rgba(139,163,148,0.3)", borderRadius: 999, padding: "2px 7px" }}>{v === "global" ? "🌍 global" : v}</span>
                ))}
              </div>
              <div style={{ fontWeight: 900, fontSize: 14, margin: "10px 0 6px" }}>{k.title}</div>
              <div style={{ fontSize: 12, color: IT.muted, lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{k.content}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: IT.muted, flex: 1 }}>{k.created_by} · {new Date(k.updated_at).toLocaleDateString("fr-FR")}</span>
                {d?.ceo && <>
                  <button data-testid={`kb-edit-${k.kb_id}`} onClick={() => setEdit(k)} style={{ ...S.ghost, padding: "5px 11px", fontSize: 11 }}>✏️</button>
                  <button data-testid={`kb-del-${k.kb_id}`} onClick={() => del(k)} style={{ ...S.ghost, padding: "5px 11px", fontSize: 11, borderColor: "rgba(248,113,113,0.35)", color: "#F87171" }}>🗑</button>
                </>}
              </div>
            </div>
          );
        })}
      </div>
      {(creating || edit) && <Editor item={edit} onClose={() => { setCreating(false); setEdit(null); }} onSaved={load} />}
    </InternalLayout>
  );
}
